import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'

export interface RelatedLink {
    to: string
    label: string
}

export default function OnThisPage({
    headings,
    related
}: {
    headings: { id: string; text: string }[]
    related: RelatedLink[]
}) {
    const [activeId, setActiveId] = useState<string>(headings[0]?.id ?? '')

    useEffect(() => {
        if (!headings.length) return
        const els = headings
            .map((h) => document.getElementById(h.id))
            .filter((el): el is HTMLElement => !!el)

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
                if (visible[0]) {
                    setActiveId(visible[0].target.id)
                }
            },
            { rootMargin: '-12% 0px -68% 0px', threshold: 0 }
        )
        els.forEach((el) => observer.observe(el))
        return () => observer.disconnect()
    }, [headings])

    return (
        <nav
            className="hidden lg:block"
            style={{ position: 'sticky', top: '6rem', alignSelf: 'start' }}
            aria-label="On this page"
        >
            <div
                className="text-[10.5px] uppercase font-semibold mb-3"
                style={{ letterSpacing: '0.18em', color: 'var(--color-ink-400)' }}
            >
                On this page
            </div>
            <ul className="flex flex-col">
                {headings.map((h) => {
                    const active = activeId === h.id
                    return (
                        <li key={h.id}>
                            <a
                                href={`#${h.id}`}
                                onClick={() => setActiveId(h.id)}
                                className="block py-2 text-[13px] leading-snug transition-all"
                                style={{
                                    color: active ? 'var(--color-gold-700)' : 'var(--color-ink-500)',
                                    fontWeight: active ? 600 : 400,
                                    borderLeft: `2px solid ${active ? 'var(--color-gold-500)' : 'var(--color-rule)'}`,
                                    paddingLeft: '0.85rem',
                                    background: active
                                        ? 'linear-gradient(90deg, var(--color-paper-100), transparent)'
                                        : 'transparent'
                                }}
                            >
                                {h.text}
                            </a>
                        </li>
                    )
                })}
            </ul>

            {related.length > 0 && (
                <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--color-rule)' }}>
                    <div
                        className="text-[10.5px] uppercase font-semibold mb-3"
                        style={{ letterSpacing: '0.18em', color: 'var(--color-ink-400)' }}
                    >
                        Related
                    </div>
                    <ul className="flex flex-col gap-0.5">
                        {related.map((r) => (
                            <li key={r.to}>
                                <Link
                                    to={r.to}
                                    className="group flex items-start gap-1.5 py-1.5 text-[13px] leading-snug transition-colors"
                                    style={{ color: 'var(--color-ink-600)' }}
                                >
                                    <ArrowUpRight
                                        size={13}
                                        className="mt-0.5 flex-shrink-0 transition-colors"
                                        style={{ color: 'var(--color-gold-700)' }}
                                    />
                                    <span className="group-hover:text-[var(--color-gold-700)] transition-colors">
                                        {r.label}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </nav>
    )
}
